import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { ImageLightbox } from '@/components/ui/ImageLightbox/ImageLightbox';
import './MessengerInlineImage.css';
import {
  computeMessengerInlinePendingSlotPx,
  readMessengerDmBubbleMaxWidthPx,
  readMessengerInlinePhotoMaxHeightPx,
  readMessengerInlinePhotoMinSidePx,
} from './messengerInlinePhotoLayout';

/** Бюджет ширины превью: как у CSS бабла — min(--messenger-dm-bubble-max-width, 85% ширины колонки ленты). */
function computeMessengerInlineColumnMaxPx(wrap: HTMLElement): number {
  const bubbleCap = readMessengerDmBubbleMaxWidthPx(wrap);
  const lanePx = (containerWidth: number) =>
    Math.min(bubbleCap, Math.floor(containerWidth * 0.85));

  const wrapper = wrap.closest('.chatAssistant__messageWrapper');
  if (wrapper && wrapper.clientWidth > 0) {
    return lanePx(wrapper.clientWidth);
  }
  if (typeof window !== 'undefined') {
    return lanePx(window.innerWidth);
  }
  return 0;
}

/** Inline-фото: слот сразу по мете (image_width/height) или по natural после загрузки — без скелетона и анимаций. */
export function MessengerInlineImage({
  src,
  alt,
  intrinsicWidth,
  intrinsicHeight,
  onSlotResize,
}: {
  src: string;
  alt?: string | null;
  intrinsicWidth?: number;
  intrinsicHeight?: number;
  /** Коллбек при изменении финального размера слота — для сообщения ширины бабла родителю */
  onSlotResize?: (widthPx: number, heightPx: number) => void;
}): React.ReactNode {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [slotWidthPx, setSlotWidthPx] = useState<number | null>(null);
  const [slotHeightPx, setSlotHeightPx] = useState<number | null>(null);
  const [isCover, setIsCover] = useState(false);

  const applySlotDimensions = useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap) return;

    const maxH = readMessengerInlinePhotoMaxHeightPx(wrap);
    const minSide = readMessengerInlinePhotoMinSidePx(wrap);
    const naturalReady = Boolean(img && img.naturalWidth > 0);

    const dimensions = naturalReady
      ? { width: img!.naturalWidth, height: img!.naturalHeight }
      : intrinsicWidth && intrinsicHeight
        ? { width: intrinsicWidth, height: intrinsicHeight }
        : null;
    if (!dimensions) {
      setSlotWidthPx(null);
      setSlotHeightPx(null);
      return;
    }

    const colMax = computeMessengerInlineColumnMaxPx(wrap);
    if (colMax <= 0) return;

    const slot = computeMessengerInlinePendingSlotPx({
      columnMaxWidth: colMax,
      imageWidth: dimensions.width,
      imageHeight: dimensions.height,
      maxHeightPx: maxH,
      minSidePx: minSide,
    });

    if (slot.widthPx <= 0 || slot.heightPx <= 0) return;

    setSlotHeightPx((prev) => (prev === slot.heightPx ? prev : slot.heightPx));
    setSlotWidthPx((prev) => (prev === slot.widthPx ? prev : slot.widthPx));
    setIsCover((prev) => (prev === slot.cover ? prev : slot.cover));

    // сообщаем родителю финальную ширину слота
    onSlotResize?.(slot.widthPx, slot.heightPx);
  }, [intrinsicWidth, intrinsicHeight, onSlotResize]);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
    } else {
      setImageLoaded(false);
    }
    applySlotDimensions();
  }, [src, intrinsicWidth, intrinsicHeight, applySlotDimensions]);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const wrapper = wrap.closest<HTMLElement>('.chatAssistant__messageWrapper');
    const outside = wrap.closest<HTMLElement>('.chatAssistant__dmOutsideLikeWrap');
    const run = () => {
      applySlotDimensions();
    };

    run();

    const ros: ResizeObserver[] = [];
    if (typeof ResizeObserver !== 'undefined') {
      if (wrapper) {
        const rw = new ResizeObserver(run);
        rw.observe(wrapper);
        ros.push(rw);
      }
      if (
        outside &&
        !outside.classList.contains('chatAssistant__dmOutsideLikeWrap_noOutside') &&
        outside !== wrapper
      ) {
        const ro = new ResizeObserver(run);
        ro.observe(outside);
        ros.push(ro);
      }
    }

    window.addEventListener('resize', run);
    return () => {
      ros.forEach((o) => o.disconnect());
      window.removeEventListener('resize', run);
    };
  }, [applySlotDimensions]);

  const wrapClassName = [
    'chatAssistant__userMsgImageWrap',
    'chatAssistant__userMsgImageWrap_fill',
    'messengerInlineImage__root',
    isCover ? 'messengerInlineImage__cover' : '',
  ]
    .filter(Boolean)
    .join(' ');


  const label = alt?.trim() ? alt : 'Вложение';

  const slotStyle: React.CSSProperties | undefined =
    slotWidthPx != null && slotHeightPx != null
      ? {
          width: `${slotWidthPx}px`,
          maxWidth: '100%',
          boxSizing: 'border-box',
          height: `${slotHeightPx}px`,
          minHeight: `${slotHeightPx}px`,
          maxHeight: 'var(--messenger-inline-photo-max-h, 420px)',
        }
      : intrinsicWidth && intrinsicHeight
        ? { aspectRatio: `${intrinsicWidth} / ${intrinsicHeight}` }
        : {
            aspectRatio: '4 / 3',
            minHeight: 'min(200px, var(--messenger-inline-photo-max-h, 420px))',
          };

  const onImgLoad = useCallback(() => {
    setImageLoaded(true);
    applySlotDimensions();
  }, [applySlotDimensions]);

  const onImgError = useCallback(() => {
    setImageLoaded(true);
    setSlotWidthPx(null);
    setSlotHeightPx(null);
  }, []);

  return (
    <>
      <div ref={wrapRef} className={wrapClassName} style={slotStyle}>
        <button
          type="button"
          className="messengerInlineImage__openBtn"
          onClick={() => setLightboxOpen(true)}
          aria-label={`Открыть предпросмотр: ${label}`}
        >
          <img
            ref={imgRef}
            src={src}
            alt={label}
            width={intrinsicWidth || undefined}
            height={intrinsicHeight || undefined}
            className={`chatAssistant__image${imageLoaded ? '' : ' messengerInlineImage__imgUntilLoaded'}`}
            onLoad={onImgLoad}
            onError={onImgError}
            draggable={false}
          />
        </button>
        {!imageLoaded ? (
          <div
            className="messengerInlineImage__loading"
            aria-busy="true"
            aria-label="Загрузка изображения"
          >
            <span className="messengerInlineImage__spinner" aria-hidden />
          </div>
        ) : null}
      </div>
      <ImageLightbox src={src} alt={label} open={lightboxOpen} onClose={() => setLightboxOpen(false)} />
    </>
  );
}
